import api from '../utils/axios';

export interface Attachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  claimId: string;
  created_by: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  creator: {
    firstName: string;
    lastName: string;
  };
  updater?: {
    firstName: string;
    lastName: string;
  };
}

export interface AttachmentsResponse {
  success: boolean;
  data: Attachment[];
}

export interface AttachmentResponse {
  success: boolean;
  data: Attachment[];
  message?: string;
}

class AttachmentsService {
  async getAttachments(claimId: string): Promise<Attachment[]> {
    const response = await api.get<AttachmentsResponse>(`/claims/${claimId}/attachments`);
    return response.data.data;
  }

  async uploadAttachment(claimId: string, files: File[]): Promise<Attachment[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await api.post<AttachmentResponse>(
      `/claims/${claimId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  async downloadAttachment(attachmentId: string): Promise<void> {
    const response = await api.get(`/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Get content type from response headers
    const contentType = response.headers['content-type'] || 'application/octet-stream';

    // Create blob with correct MIME type
    const blob = new Blob([response.data as BlobPart], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getAttachmentPreviewUrl(attachmentId: string): Promise<string> {
    const response = await api.get(`/attachments/${attachmentId}/download?preview=true`, {
      responseType: 'blob',
    });
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const blob = new Blob([response.data as BlobPart], { type: contentType });
    return window.URL.createObjectURL(blob);
  }

  async previewAttachment(attachmentId: string, mimeType: string): Promise<string | null> {
    if (this.isPreviewable(mimeType)) {
      return await this.getAttachmentPreviewUrl(attachmentId);
    }
    return null;
  }

  isPreviewable(mimeType: string): boolean {
    const previewableTypes = ['image/', 'application/pdf', 'text/', 'audio/', 'video/'];
    return previewableTypes.some(type => mimeType.startsWith(type));
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    await api.delete(`/attachments/${attachmentId}`);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📈';
    if (mimeType.includes('zip') || mimeType.includes('rar')) return '🗜️';
    return '📎';
  }
}

export default new AttachmentsService(); 